import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  getUserById,
  deleteUserById,
  blockUser,
  unblockUser,
  setUserEmail,
  setUserPassword,
  getUsersByEmail,
  updateUserGender,
  updateUserName,
  getUserGifts,
  createUserGift,
  consumeUserGift,
  getSubsicriptionHistory,
  addCustomerProduct,
  expireCustomerProduct,
  getProductByPurchaseSourceAndProductType,
  getUserPoints,
  addPointsToUser,
  getUserPlans,
  searchUsersByFilter,
  getFcmTokenByUserId,
} from './thunks';
import { CustomerCareState } from './types';

export const name = 'customerCare';

const initialState: CustomerCareState = {
  data: [],
  details: null,
  subscriptionHistory: [],
  userPlans: null,
  gifts: [],
  userPoints: 0,
  products: [],
  status: 'idle',
  updatingStatus: 'idle',
  subscriptionStatus: 'idle',
  giftStatus: 'idle',
  searchUsersByFilter: {
    data: [],
    status: 'idle',
  },
  fcmTokenByUserId: {
    data: null,
    status: 'idle',
  },
};

export const deleteUserAsync = createAsyncThunk(
  name + '/deleteUser',
  deleteUserById
);
export const getUserByIdAsync = createAsyncThunk(
  name + '/getUserById',
  getUserById
);
export const blockUserAsync = createAsyncThunk(name + '/blockUser', blockUser);

export const unblockUserAsync = createAsyncThunk(
  name + '/unblockUser',
  unblockUser
);
export const setUserEmailAsync = createAsyncThunk(
  name + '/setUserEmail',
  setUserEmail
);
export const setUserPasswordAsync = createAsyncThunk(
  name + '/setUserPassword',
  setUserPassword
);
export const getUsersByEmailAsync = createAsyncThunk(
  name + '/getUsersByEmail',
  getUsersByEmail
);
export const updateUserGenderAsync = createAsyncThunk(
  name + '/updateUserGender',
  updateUserGender
);
export const updateUserNameAsync = createAsyncThunk(
  name + '/updateUserName',
  updateUserName
);
export const getUserGiftsAsync = createAsyncThunk(
  name + '/getUserGifts',
  getUserGifts
);
export const createUserGiftAsync = createAsyncThunk(
  name + '/createUserGift',
  createUserGift
);
export const consumeUserGiftAsync = createAsyncThunk(
  name + '/consumeUserGift',
  consumeUserGift
);
export const getSubsicriptionHistoryAsync = createAsyncThunk(
  name + '/getSubsicriptionHistory',
  getSubsicriptionHistory
);
export const addCustomerProductAsync = createAsyncThunk(
  name + '/addCustomerProduct',
  addCustomerProduct
);
export const expireCustomerProductAsync = createAsyncThunk(
  name + '/expireCustomerProduct',
  expireCustomerProduct
);
export const getProductByPurchaseSourceAndProductTypeAsync = createAsyncThunk(
  name + '/getProductByPurchaseSourceAndProductType',
  getProductByPurchaseSourceAndProductType
);

export const getUserPointsAsync = createAsyncThunk(
  name + '/getUserPoints',
  getUserPoints
);
export const addPointsToUserAsync = createAsyncThunk(
  name + '/addPointsToUser',
  addPointsToUser
);
export const getUserPlansAsync = createAsyncThunk(
  name + '/getUserPlans',
  getUserPlans
);

export const searchUsersByFilterAsync = createAsyncThunk(
  name + '/searchUsersByFilter',
  searchUsersByFilter
);

export const getFcmTokenByUserIdAsync = createAsyncThunk(
  name + '/getFcmTokenByUserId',
  getFcmTokenByUserId
);

export const slice = createSlice({
  name: name,
  initialState,
  reducers: {
    clear: () => {
      return initialState;
    },
    clearDetails: (state) => {
      state.details = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUsersByEmailAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getUsersByEmailAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.data = action.payload;
      })
      .addCase(getUsersByEmailAsync.rejected, (state) => {
        state.status = 'failed';
      })
      .addCase(getUserByIdAsync.pending, (state) => {
        state.updatingStatus = 'loading';
      })
      .addCase(getUserByIdAsync.fulfilled, (state, action) => {
        state.updatingStatus = 'idle';
        state.details = action.payload;
      })
      .addCase(getUserByIdAsync.rejected, (state) => {
        state.updatingStatus = 'failed';
      })
      .addCase(getSubsicriptionHistoryAsync.pending, (state) => {
        state.subscriptionStatus = 'loading';
      })
      .addCase(getSubsicriptionHistoryAsync.fulfilled, (state, action) => {
        state.subscriptionStatus = 'idle';
        state.subscriptionHistory = action.payload;
      })
      .addCase(getSubsicriptionHistoryAsync.rejected, (state) => {
        state.subscriptionStatus = 'failed';
      })
      .addCase(getUserGiftsAsync.pending, (state) => {
        state.giftStatus = 'loading';
      })
      .addCase(getUserGiftsAsync.fulfilled, (state, action) => {
        state.giftStatus = 'idle';
        state.gifts = action.payload;
      })
      .addCase(getUserGiftsAsync.rejected, (state) => {
        state.giftStatus = 'failed';
      })
      .addCase(
        getProductByPurchaseSourceAndProductTypeAsync.fulfilled,
        (state, action) => {
          state.products = action.payload;
        }
      )
      .addCase(getUserPointsAsync.fulfilled, (state, action) => {
        state.userPoints = action.payload;
      })
      .addCase(getUserPlansAsync.fulfilled, (state, action) => {
        state.userPlans = action.payload;
      })
      .addCase(searchUsersByFilterAsync.pending, (state) => {
        state.searchUsersByFilter.status = 'loading';
      })
      .addCase(searchUsersByFilterAsync.fulfilled, (state, action) => {
        state.searchUsersByFilter = {
          data: action.payload,
          status: 'idle',
        };
      })
      .addCase(searchUsersByFilterAsync.rejected, (state) => {
        state.searchUsersByFilter.status = 'failed';
      })
      .addCase(getFcmTokenByUserIdAsync.pending, (state) => {
        state.fcmTokenByUserId.status = 'loading';
      })
      .addCase(getFcmTokenByUserIdAsync.fulfilled, (state, action) => {
        state.fcmTokenByUserId = {
          data: action.payload,
          status: 'idle',
        };
      })
      .addCase(getFcmTokenByUserIdAsync.rejected, (state) => {
        state.fcmTokenByUserId.status = 'failed';
      });
  },
});

export const actions = slice.actions;

export default slice.reducer;
